import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const AffiliatesList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    notes: '',
    commissionPercentage: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    fetchAffiliates();
  }, [searchTerm]);

  const fetchAffiliates = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiRequest(
        `/admin/affiliates?limit=200${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`
      );
      if (data && data.success) {
        setList(data.data || []);
      } else {
        setError(data?.message || 'حدث خطأ في جلب الشركاء');
      }
    } catch (err) {
      setError('حدث خطأ في الاتصال بالخادم');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingAffiliate(null);
    setFormData({
      name: '',
      code: '',
      email: '',
      phone: '',
      notes: '',
      commissionPercentage: '',
      isActive: true,
    });
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (affiliate) => {
    setEditingAffiliate(affiliate);
    setFormData({
      name: affiliate.name || '',
      code: affiliate.code || '',
      email: affiliate.email || '',
      phone: affiliate.phone || '',
      notes: affiliate.notes || '',
      commissionPercentage: affiliate.commissionPercentage !== undefined && affiliate.commissionPercentage !== null ? String(affiliate.commissionPercentage) : '',
      isActive: affiliate.isActive !== false,
    });
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAffiliate(null);
    setModalError('');
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [e.target.name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        commissionPercentage: formData.commissionPercentage !== '' ? Number(formData.commissionPercentage) : 0,
        isActive: formData.isActive,
      };
      if (editingAffiliate) {
        const data = await apiRequest(`/admin/affiliates/${editingAffiliate._id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (data && data.success) {
          closeModal();
          fetchAffiliates();
        } else {
          setModalError(data?.message || 'فشل التحديث');
        }
      } else {
        const data = await apiRequest('/admin/affiliates', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (data && data.success) {
          closeModal();
          fetchAffiliates();
        } else {
          setModalError(data?.message || 'فشل الإنشاء');
        }
      }
    } catch (err) {
      setModalError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (affiliate) => {
    if (!window.confirm(`حذف الشريك "${affiliate.name}"؟ سيتم إلغاء ربط العملاء به.`)) return;
    try {
      const data = await apiRequest(`/admin/affiliates/${affiliate._id}`, { method: 'DELETE' });
      if (data && data.success) {
        fetchAffiliates();
      } else {
        setError(data?.message || 'فشل الحذف');
      }
    } catch (err) {
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  if (loading && list.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">إدارة الشركاء (المسوقون بالعمولة)</h2>
          <div className="flex gap-2" style={{ alignItems: 'center' }}>
            <input
              type="text"
              placeholder="بحث (الاسم، الكود، البريد)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
              style={{ width: '260px' }}
            />
            <button className="btn btn-success btn-sm" onClick={openCreate}>
              ➕ إضافة شريك
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <span>❌</span>
            <span>{error}</span>
          </div>
        )}

        {list.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🤝</div>
            <div className="empty-state-title">لا يوجد شركاء</div>
            <div className="empty-state-text">يمكنك إضافة شريك (مسوق بالعمولة) ثم ربط العملاء به من صفحة العميل</div>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الكود</th>
                  <th>البريد</th>
                  <th>الهاتف</th>
                  <th>نسبة العمولة %</th>
                  <th>عدد العملاء</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a._id}>
                    <td><strong>{a.name || '—'}</strong></td>
                    <td>{a.code || '—'}</td>
                    <td>{a.email || '—'}</td>
                    <td>{a.phone || '—'}</td>
                    <td>{a.commissionPercentage != null ? `${a.commissionPercentage}%` : '—'}</td>
                    <td>
                      <span className="badge badge-info">{a.customerCount ?? 0}</span>
                    </td>
                    <td>
                      <span className={`badge ${a.isActive !== false ? 'badge-success' : 'badge-danger'}`}>
                        {a.isActive !== false ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => openEdit(a)}
                      >
                        تعديل
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        style={{ marginLeft: '0.5rem' }}
                        onClick={() => handleDelete(a)}
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 92vw)', minWidth: '400px' }}>
            <div className="card-header">
              <h2 className="card-title">{editingAffiliate ? 'تعديل الشريك' : 'إضافة شريك جديد'}</h2>
              <button type="button" className="btn btn-secondary btn-sm" onClick={closeModal}>
                إلغاء
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {modalError && (
                <div className="alert alert-danger" style={{ margin: '0 1.5rem 1rem' }}>
                  {modalError}
                </div>
              )}
              <div style={{ padding: '0 1.5rem 1.5rem' }}>
                <div className="form-group">
                  <label className="form-label">الاسم *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">الكود (اختياري)</label>
                  <input
                    type="text"
                    name="code"
                    className="form-control"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="مثال: AFF01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">البريد الإلكتروني</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">الهاتف</label>
                  <input
                    type="text"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">نسبة العمولة (%)</label>
                  <input
                    type="number"
                    name="commissionPercentage"
                    className="form-control"
                    value={formData.commissionPercentage}
                    onChange={handleChange}
                    min={0}
                    max={100}
                    step={0.5}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">ملاحظات</label>
                  <textarea
                    name="notes"
                    className="form-control"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      style={{ marginLeft: '0.5rem' }}
                    />
                    نشط
                  </label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    إلغاء
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'جاري الحفظ...' : (editingAffiliate ? 'حفظ التعديلات' : 'إضافة')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatesList;
